import React, { useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { useDataWithRoute, useTitleFromComponent, useUrlState } from "../../../utils/hooks";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { VsiBubble, VsiH4 } from "../../../styled/App.styled";
import { Card } from "reactstrap";
import Grid from "../../../components/Grid";

import VsiLabeledSelect from "../../../components/VsiLabeledSelect";

import {
    allDeviceDefs,
    allPoolsDefs,
    allVolumesDefs,
    allNodesDefs,
    allLunsDefs,
    allSharesDefs,
} from "../../../utils/gridColumnDefs/storage/allUnits";

const allUnits = [
    {value: 'devices', label: 'Devices', route: 'enterprise_devices_proc'},
    {value: 'pools', label: 'Pools', route: 'enterprise_pools_proc'},
    {value: 'volumes', label: 'Volumes', route: 'enterprise_volumes_proc'},
    {value: 'luns', label: 'LUNs', route: 'enterprise_luns_proc'},
    {value: 'shares', label: 'Shares', route: 'enterprise_shares_proc'},
    {value: 'nodes', label: 'Nodes', route: 'enterprise_nodes_proc'},
];

function returnActiveDef(unit) {
    switch (unit) {
        case 'shares':
            return allSharesDefs;
        case 'luns':
            return allLunsDefs;
        case 'nodes':
            return allNodesDefs;
        case 'volumes':
            return allVolumesDefs;
        case 'pools':
            return allPoolsDefs;
        case 'devices':
        default:
            return allDeviceDefs;
    }
}

export default function AllUnits({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client: { clientId }, periodId } = useVsiAppContext();
    const [params, setParams] = useUrlState({
        unit: allUnits[0].value,
    });
    const { unit } = params;
    const navigate = useNavigate();

    const selectedUnit = useMemo(() => {
        return allUnits.find(({value}) => unit === value) || allUnits[0];
    }, [unit]);

    const { route } = selectedUnit;

    const activeRoute = useMemo(() => `/api/v6/report/${route}?client_id=${clientId}&period_id=${periodId}`, [route, clientId, periodId])
    const [{ recordset: allUnitsData }, { isLoading }] = useDataWithRoute(activeRoute);

    const gridText = useMemo(() => {
        if (unit === "devices") return `Double click to see detailed ${unit.slice(0, unit.length - 1)} reports`;
        return '';
    }, [unit]);

    const activeDef = useMemo(() => {
        return returnActiveDef(unit) || [];
    }, [unit]);

    const onUnitChange = useCallback((selectedUnit) => {
        setParams({unit: selectedUnit.value});
    }, [setParams]);

    const onRowClick = useCallback((e) => {
        if (unit === "devices") {
            switch (e?.data?.context?.toLowerCase()) {
                case "switch":
                    navigate(`/reports/san-switch/switch/isl-port-analysis?switch_name=${encodeURI(e.data.device_name)}&period_id=${e.data.period_id}`);
                    break;
                case "device":
                    navigate(`/reports/storage/device/device-summary?device_name=${encodeURI(e.data.device_name)}&wwn=${encodeURI(e?.data?.wwn ?? e.data.device_name)}&period_id=${e.data.period_id}`);
                    break;
                default:
                // do nothing
            }
        } else if (unit === "volumes") {
            navigate(`/reports/storage/device/device-trends?device_name=${encodeURI(e.data.device_name)}&wwn=${encodeURI(e.data.wwn)}&period_id=${e.data.period_id}&metric=by_volume&volume=${e.data.volume_name}`)
        } else if (unit === "shares") {
            navigate(`/reports/storage/device/device-trends?device_name=${encodeURI(e.data.device_name)}&wwn=${encodeURI(e.data.wwn)}&period_id=${e.data.period_id}&metric=by_share&share=${e.data.share_name}&navi_share=${e.data.share_name}`)
        }
    }, [navigate, unit]);

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>All Units - {selectedUnit.label}</VsiH4>
        <VsiBubble noAutobot>
            <VsiLabeledSelect options={allUnits}
                              isDisabled={isLoading}
                              isLoading={isLoading}
                              label={'Select Unit'}
                              onChange={onUnitChange}
                              defaultValue={selectedUnit}/>
        </VsiBubble>
        <VsiBubble unit={unit.label}>
            <Grid data={allUnitsData}
                  isLoading={isLoading}
                  columnDefs={activeDef}
                  saveParams={{ unit: unit.value }}
                  rowDoubleClicked={onRowClick}
                  gridText={gridText}/>
        </VsiBubble>
    </Card>
}
